import React, { useState } from 'react';
import {
  GetCollectionSession,
  RecordingStatus,
  GetRecordingSuccess,
} from '@scale/shared/webinarSessions/types';
import { Icon, DropdownMenu, Link, Button, Spinner, Text } from '@scale/scaleui-radix';
import { faVideo } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from 'react-query';
import { fetchWebinarSessionRecording } from '../api';
import WebinarError from './WebinarError';

interface Props {
  session: GetCollectionSession;
}

const RecordingMenu: React.FC<{ recording: GetRecordingSuccess }> = ({ recording }) => {
  const files = recording.recordingFiles.map(file => {
    let url = file.download_url;
    let isDownload = true;

    if (file.play_url) {
      url = `${file.play_url}?pwd=${recording.recordingPlayPasscode}`;
      isDownload = false;
    }

    url = url.replace('remotasks', 'outlier-ai');

    return {
      id: file.id,
      recording_type: file.recording_type.split('_').join(' '),
      url,
      isDownload,
    };
  });

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <Button variant="outline" color="neutral" size="1">
          Recording links
          <DropdownMenu.TriggerIcon />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content variant="soft" color="accent">
        {files.map(({ id, url, recording_type, isDownload }) => (
          <DropdownMenu.Item key={id} asChild>
            <Link href={url} target="_blank" rel="noopener noreferrer">
              {isDownload ? 'Download recording' : 'View recording'} ({recording_type})
            </Link>
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

const WebinarDetailRecordings: React.FC<Props> = ({ session }) => {
  const [requested, setRequested] = useState(false);

  const { data, isLoading, error } = useQuery(
    ['zoom-recording', session._id],
    () => fetchWebinarSessionRecording(session._id),
    {
      staleTime: 60 * 60 * 1000,
      enabled: requested,
    },
  );

  if (error) {
    return <WebinarError error={error} />;
  }

  const recording = data?.recording;

  return (
    <div className="flex items-center gap-2">
      <Icon color="neutral" icon={faVideo} size="2" label="recording" />
      {(() => {
        if (isLoading) {
          return <Spinner />;
        }

        if (!recording) {
          return (
            <Button size="1" variant="outline" onClick={() => setRequested(true)}>
              Get recording
            </Button>
          );
        }

        if (recording.recordingStatus === RecordingStatus.DOES_NOT_EXIST) {
          return (
            <Text size="2" color="neutral">
              Meeting was not recorded
            </Text>
          );
        }

        return <RecordingMenu recording={recording} />;
      })()}
    </div>
  );
};

export default WebinarDetailRecordings;
